import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link, Container, Typography, Tooltip, Chip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const useStyles = makeStyles((theme) => ({
  footer_container: {
    padding: '2px 7px 2px 64px',
    display: 'flex',
    justifyContent: 'space-between',
    height: '2.5vh',
    minWidth: '100vw',
    position: 'fixed',
    bottom: 0,
    left: 0,
    zIndex: '100',
    alignItems: 'center',
    backgroundColor: theme.palette.background.mainDark,
  },
  linkText: {
    color: theme.palette.texts.secondary,
    fontSize: theme.fonts.responsiveSmall,
  },
  link: {
    textDecoration: 'underline',
  },

  sms_logo: { width: 'auto', height: '100%', marginRight: '10px' },

  text_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    width: '100%',
  },
  username_text: {
    fontSize: theme.fonts.responsiveSmall,
    letterSpacing: '0.1rem',
    color: theme.palette.accents.main,
  },

  avatar_profile_text: {
    fontSize: theme.typography.h6.fontSize,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.accents.main,
  },

  aliases_container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
  },

  aliases_chips: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },

  listHeader: {
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.primary,
  },

  info_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    margin: '8px 2px',
  },

  user_info_container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    border: '1px solid gray',
    padding: '6px',
    borderRadius: '5px',
    marginBottom: '20px',
  },

  user_info_title: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.primary,
    width: '50%',
    fontWeight: 'bold',
  },

  user_info_text: {
    fontSize: theme.fonts.responsiveMediumSmall,
    letterSpacing: '0.1rem',
    color: theme.palette.texts.primary,
    width: '50%',
  },

  demo_text: {
    color: theme.palette.accents.main,
    padding: '0.5rem',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    zIndex: theme.zIndex.tooltip,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function Copyright() {
  const classes = useStyles();
  const { t } = useTranslation();
  const darkState = useSelector((store) => store.gui.darkState);
  const globalSettings = useSelector((store) => store.global_settings ?? {});
  const userAliases = localStorage.getItem('aliases') ?? null;
  const annotationPermission = localStorage.getItem('annotation') ?? null;
  const diagnosticsPermission = localStorage.getItem('diagnostics') ?? null;
  const lastLogin = localStorage.getItem('last_login') ?? null;
  const role = localStorage.getItem('role') ?? null;
  const username = localStorage.getItem('username') ?? null;
  const { client_id, client } = globalSettings;
  const isDevURL =
    window.location.hostname === 'localhost' ||
    window.location.hostname === 'dev.b-rayz.ch' ||
    window.location.hostname === 'demo.b-rayz.ch' ||
    window.location.hostname === 'partner.b-rayz.ch' ||
    process.env?.REACT_APP_SOFTWARE_VERSION?.includes('-dev');

  return (
    <Container maxWidth={'xl'} className={classes.footer_container}>
      {/* Show only in Demo or Dev versions */}
      {isDevURL && <Typography className={classes.demo_text}>DEMO</Typography>}

      <div className={classes.text_container}>
        <Typography className={classes.linkText} align='center'>
          {t('Layout.footer_disclaimer_important')}
          <Link className={classes.link} color='inherit' href='https://b-rayZ.ch/'>
            b-rayZ AG {new Date().getFullYear()}
          </Link>
          {'©'}
        </Typography>
      </div>
      <div style={{ marginRight: '12px', display: 'flex', alignItems: 'flex-end', cursor: 'help' }}>
        <Tooltip
          arrow
          placement='top'
          title={
            <div className={classes.user_info_container}>
              <Typography className={classes.avatar_profile_text}>{t('UserPanel.user_info')}</Typography>
              <div className={classes.info_container}>
                <Typography className={classes.user_info_title}>{t('LoginRegister.username')}:</Typography>
                <Typography className={classes.user_info_text}>{localStorage.getItem('username')}</Typography>
              </div>
              <div className={classes.info_container}>
                <Typography className={classes.user_info_title}>{t('LoginRegister.role')}:</Typography>
                <Typography className={classes.user_info_text}>{role ? role : '-'}</Typography>
              </div>
              <div className={classes.info_container}>
                <Typography className={classes.user_info_title}>DANAI:</Typography>
                <Typography className={classes.user_info_text}>
                  {annotationPermission === 'true' ? t('UserPanel.annotating') : t('UserPanel.no_annotating')}
                </Typography>
              </div>
              <div className={classes.info_container}>
                <Typography className={classes.user_info_title}>{t('UserPanel.b_diagnostics')}:</Typography>
                <Typography className={classes.user_info_text}>
                  {diagnosticsPermission === 'true' ? t('UserPanel.enable') : t('UserPanel.disable')}
                </Typography>
              </div>
              <div className={classes.info_container}>
                <Typography className={classes.user_info_title}>{t('users.lastlogin')}:</Typography>
                <Typography className={classes.user_info_text}>{lastLogin ? lastLogin : '-'}</Typography>
              </div>
              {userAliases && userAliases !== 'undefined' && (
                <div className={classes.aliases_container}>
                  <div className={classes.aliases_chips}>
                    <Typography className={classes.listHeader}>{t('users.dicomTag')}</Typography>
                  </div>
                  <div>
                    {userAliases?.split(';').map((alias, i) => (
                      <Chip
                        key={i}
                        style={{ margin: '6px' }}
                        size='small'
                        label={alias.trim() ? alias.trim() : '-'}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #808080',
                  alignItems: 'center',
                  marginTop: '4px',
                }}>
                <Typography
                  style={{
                    margin: '0',
                    fontStyle: 'italic',
                    color: '#808080',
                    fontSize: '11px',
                  }}>
                  {client} - {client_id}
                </Typography>
              </div>
            </div>
          }>
          <Typography className={classes.username_text}>{localStorage.getItem('username')}</Typography>
        </Tooltip>
      </div>
    </Container>
  );
}
